import {createPortal} from "react-dom";
import {useState, useEffect, useRef, useCallback} from "react";
import {
  Play,
  Pause,
  Volume2,
  VolumeX,
  X,
  Maximize,
  Minimize,
  Lock,
} from "lucide-react";

import {formatPlaybackTime} from "../../../utils/datetime";
import getProgressData from "../utils/getProgressData";
import getFurthestPosition from "../utils/getFurthestPosition";
import creditedSeconds from "../utils/creditedSeconds";

const isYouTubeUrl = (url) =>
  !!url && (url.includes("youtube.com") || url.includes("youtu.be"));

const VideoModal = ({
  video,
  watchHistory,
  onClose,
  recentVideos,
  onProgress,
  onPlay, // Ensure this is called when the modal video starts playing
  playingId, // NEW: currently playing video id from parent
  onSwitchVideo, // NEW: swap modal to a different video (suggestion click)
  queue = [], // NEW: ordered playlist this video belongs to (up-next + autoplay)
}) => {
  const videoRef = useRef(null);
  const ytContainerRef = useRef(null);
  const playerRef = useRef(null);
  const hlsRef = useRef(null);
  // The element we put into the browser's native fullscreen and/or expand to
  // fill the viewport via CSS. Wraps the video/iframe + the controls overlay
  // so the controls stay reachable in fullscreen.
  const playerWrapRef = useRef(null);
  // A play tap that lands before the player has finished initializing
  // (most common for a fresh "Not Started" video the user opens and taps
  // right away) used to be silently dropped by the `isPlayerReady` guard
  // in handleTogglePlay. Remember the intent here and honor it the moment
  // the player signals ready.
  const pendingPlayRef = useRef(false);

  // Round-3 #4 (player loops / balloons memory): the previous
  // implementation took `savedTime` from `watchHistory[video.id]` on every
  // render and passed it into the init `useEffect` dep array. Because the
  // parent calls `onProgress` ~once per second of playback, that updates
  // `watchHistory` -> `savedTime` changes -> the init effect re-runs ->
  // cleanupPlayers() destroys the player and rebuilds it from the new
  // savedTime -> the new player starts -> next second `savedTime` changes
  // again -> infinite re-init loop, growing memory each cycle.
  //
  // Fix: capture `savedTime`, `onProgress`, `onPlay` in refs. The init
  // effect now depends only on the *identity* of the video (id + url).
  // The live values are still read via the refs on every tick, so progress
  // reporting stays accurate.
  const initialSavedTimeRef = useRef(watchHistory[video.id]?.lastPosition || 0);
  const lastTimeRef = useRef(initialSavedTimeRef.current);
  // The furthest point the viewer has actually reached in the CURRENT video.
  // Orientation/intro videos may be rewound freely but not fast-forwarded
  // past this high-water mark, so the participant can't skip required content.
  // Seeded from saved progress and bumped on every playback tick.
  const maxReachedRef = useRef(initialSavedTimeRef.current);
  const onProgressRef = useRef(onProgress);
  const onPlayRef = useRef(onPlay);
  // When a play tap lands before the player is ready, the resulting play
  // call fires from an async ready/manifest callback -- i.e. OUTSIDE any
  // user gesture -- so the browser autoplay policy blocks unmuted
  // playback. (This is the intermittent "open a Not Started video and tap
  // right away -> it never plays" report: when the tap beats the player's
  // initialization, the queued play is gesture-less and gets blocked.)
  // We work around it by starting that deferred play MUTED (always
  // allowed) and unmuting the instant playback actually begins. This ref
  // tracks the in-flight muted-autoplay handshake so the volume-sync
  // effect doesn't fight it and so we know to unmute on the first PLAYING
  // signal. The synchronous (already-ready) tap path keeps its real user
  // gesture and is unaffected.
  const deferredMutedRef = useRef(false);
  // Live mirror of `isMuted` for the async player callbacks, which close
  // over a stale render's value.
  const isMutedRef = useRef(false);

  // Seconds left on the auto-advance countdown shown when a video ends and the
  // playlist has a next video. null = not counting down.
  const [countdown, setCountdown] = useState(null);

  // Set true right before an auto-advance / "Play now" switch so the next
  // video starts playing on its own once its player is ready, instead of
  // loading paused like a normal open. Read in the init effect below.
  const autoplayNextRef = useRef(false);

  // The id of the currently-loaded video. The YouTube onStateChange handler is
  // created once and reused across a loadVideoById swap, so it must read the
  // CURRENT id from here (not its stale closure) to log progress/ended for the
  // right video.
  const videoIdRef = useRef(video.id);
  // Set true right before a YouTube->YouTube auto-advance: keep the existing
  // (already user-activated) player and swap the video via loadVideoById,
  // instead of building a fresh iframe. A new iframe cannot autoplay on
  // iOS/WebKit (no user activation); reusing the player carries it forward.
  const reuseRef = useRef(false);

  useEffect(() => {
    onProgressRef.current = onProgress;
  }, [onProgress]);
  useEffect(() => {
    onPlayRef.current = onPlay;
  }, [onPlay]);

  // Reset the captured savedTime whenever the modal is asked to show a
  // different video (id change). We do NOT capture the ever-changing
  // value during playback.
  useEffect(() => {
    initialSavedTimeRef.current = watchHistory[video.id]?.lastPosition || 0;
    lastTimeRef.current = initialSavedTimeRef.current;
    maxReachedRef.current = initialSavedTimeRef.current;
    videoIdRef.current = video.id;
    setCaptionTracks([]); // caption data is per-video; refetched on the new one
    setCaptionStatus("unknown");
    setCaptionNotice(null);
    setHasPlayedOnce(false);
    hasPlayedRef.current = false;
    captionVerifyTimersRef.current.forEach(clearTimeout);
    captionVerifyTimersRef.current = [];
    // Carry the viewer's caption choice onto the incoming video.
    pendingCaptionRef.current = captionsOnRef.current;
    setCountdown(null); // cancel any pending auto-advance from the prior video
    // Intentionally not including `watchHistory` -- see comment above.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [video.id]);

  const [isPlaying, setIsPlaying] = useState(false);
  const [isPlayerReady, setIsPlayerReady] = useState(false);
  const [duration, setDuration] = useState(0);
  const [currentTime, setCurrentTime] = useState(initialSavedTimeRef.current);
  const [isMuted, setIsMuted] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  // Controls (progress bar + buttons) visibility. Auto-hides while playing
  // so fullscreen stays clean; any tap brings it back.
  const [controlsVisible, setControlsVisible] = useState(true);
  // The YouTube channel name. Our video records don't carry it, so we pull it
  // from YouTube's public oEmbed endpoint (author_name) when the modal opens.
  // Empty for non-YouTube videos or if the lookup fails.
  const [channel, setChannel] = useState("");

  // Playback speed. Applies to every video (YouTube + HTML5). The preference
  // is kept across video switches (see the sync effect) so a viewer who picks
  // 1.5x keeps it for the next clip.
  const PLAYBACK_RATES = [1, 1.5, 2];
  const [playbackRate, setPlaybackRate] = useState(1);
  const playbackRateRef = useRef(1);

  // Captions. Off by default; the CC button opens a language menu (Off /
  // English / Español / any other tracks the video carries). YouTube videos
  // are driven through the player's caption module on the LIVE player -- no
  // iframe rebuild -- because the cc_* player vars are only preferences
  // YouTube may ignore (Spanish silently fell back to English) and rebuilds
  // raced the module load (captions often never appeared).
  //   captionLang: { code: 'es', label: 'Español' }; null = default track.
  const [captionsOn, setCaptionsOn] = useState(false);
  const [captionTracks, setCaptionTracks] = useState([]);
  const [captionLang, setCaptionLang] = useState(null);
  const [showCaptionMenu, setShowCaptionMenu] = useState(false);
  // 'unknown' until the caption module reports in; 'ready' once the video
  // shows at least one track; 'none' when the video provably has no captions
  // (drives the "No captions available" menu state).
  const [captionStatus, setCaptionStatus] = useState("unknown");
  // Transient on-player notice ("No captions are available…") shown when the
  // no-captions verdict lands AFTER the viewer already picked a language.
  const [captionNotice, setCaptionNotice] = useState(null);
  // State twin of hasPlayedRef: re-arms the availability probe on first play
  // (the ref alone can't trigger the effect).
  const [hasPlayedOnce, setHasPlayedOnce] = useState(false);
  // Live mirrors for the async player callbacks.
  const captionsOnRef = useRef(false);
  const captionLangRef = useRef(null);
  // A selection made before the caption module was up; applied from
  // onApiChange / the probe timers, where the tracklist actually exists.
  const pendingCaptionRef = useRef(false);
  // Whether THIS video has reached PLAYING at least once. "No captions" is
  // only trustworthy after playback starts -- the tracklist stays empty on a
  // never-started player.
  const hasPlayedRef = useRef(false);
  // The caption helpers live below the player-init effect; the player's
  // event closures reach them through refs so they never go stale.
  const readCaptionTracksRef = useRef(null);
  const applyCaptionSelectionRef = useRef(null);
  // Pending verify-and-retry timers for a caption selection (see
  // applyCaptionSelection); cleared on every new selection / video switch.
  const captionVerifyTimersRef = useRef([]);

  const isYouTube =
    video.url.includes("youtube.com") || video.url.includes("youtu.be");

  // Orientation/intro videos are the program's required first watch, so they
  // can't be fast-forwarded. Same rule the catalog uses to pin them (taxonomy
  // tagVideo): an explicit is_intro flag or the legacy "Orientation" category.
  const isOrientation =
    video.is_intro === true ||
    (video.category || "").toLowerCase().trim() === "orientation";

  // We can reuse the live player (loadVideoById) only when the next video is
  // YouTube and a YouTube player already exists (its presence implies the
  // current video is YouTube too). Uses only refs + a pure helper, so callers
  // in deps-light effects don't go stale. This is the only path that keeps
  // autoplay working on iOS.
  const canReuse = (next) =>
    isYouTubeUrl(next?.url) && !!playerRef.current?.loadVideoById;
  const canReuseRef = useRef(canReuse);
  canReuseRef.current = canReuse;

  // Look up the YouTube channel name from the public oEmbed endpoint whenever
  // the loaded video changes. oEmbed is CORS-enabled and needs no auth, and
  // (unlike the player's getVideoData) returns author_name without the video
  // having to start playing. `cancelled` guards against a stale response
  // landing after the modal has switched videos.
  useEffect(() => {
    setChannel("");
    if (!isYouTube) return;
    let cancelled = false;
    fetch(
      `https://www.youtube.com/oembed?url=${encodeURIComponent(
        video.url,
      )}&format=json`,
    )
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => {
        if (!cancelled && d?.author_name) setChannel(d.author_name);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [video.url, isYouTube]);

  // Live label/classes/dot reflect the furthest position reached; read off
  // `watchHistory` directly so the progress chip stays accurate without
  // re-running the init effect. Furthest (not the resume position) so a
  // rewatch can't flip a Completed/Watched chip back to In Progress.
  const liveSavedTime = getFurthestPosition(watchHistory[video.id]);
  const {label, classes, dot} = getProgressData(liveSavedTime, video);

  // "Up next" is the rest of the current playlist after this video. When the
  // modal was opened outside a playlist (search/grid), `queue` is empty and we
  // fall back to the recently-watched suggestions so the panel isn't empty.
  const idxInQueue = queue.findIndex((v) => v?.id === video.id);
  const upNext =
    idxInQueue >= 0
      ? queue
          .slice(idxInQueue + 1)
          .filter((v) => v && v.url && v.id !== video.id)
      : [];
  const nextVideo = upNext[0] || null;
  const fromQueue = upNext.length > 0;
  // The whole rest of the playlist (or the recently-watched fallback). The
  // list scrolls and its thumbnails lazy-load, so a long playlist is fine.
  const suggestions = fromQueue
    ? upNext
    : recentVideos.filter((v) => v.id !== video.id && v.url);

  // The end-of-video handlers live inside the init effect's player callbacks,
  // which close over the first render. Read the live "next" through a ref and
  // expose a stable starter the player callbacks can call on ENDED.
  const nextVideoRef = useRef(null);
  useEffect(() => {
    nextVideoRef.current = nextVideo;
  }, [nextVideo]);
  const beginCountdown = useCallback(() => {
    if (nextVideoRef.current) setCountdown(5);
  }, []);
  const beginCountdownRef = useRef(beginCountdown);
  useEffect(() => {
    beginCountdownRef.current = beginCountdown;
  }, [beginCountdown]);

  // Read onSwitchVideo through a ref so the tick effect below depends ONLY on
  // `countdown`. The parent passes a fresh onSwitchVideo identity every render,
  // and the activity heartbeat re-renders the dashboard every second -- if it
  // were a dep, each re-render would clear and reset the 1s timer before it
  // fired, freezing the countdown at its starting value.
  const onSwitchVideoRef = useRef(onSwitchVideo);
  useEffect(() => {
    onSwitchVideoRef.current = onSwitchVideo;
  }, [onSwitchVideo]);

  // Tick the auto-advance countdown once per second; at 0, switch to the next
  // video in the playlist.
  useEffect(() => {
    if (countdown == null) return undefined;
    if (countdown <= 0) {
      const next = nextVideoRef.current;
      setCountdown(null);
      if (next && onSwitchVideoRef.current) {
        autoplayNextRef.current = true;
        if (canReuseRef.current(next)) reuseRef.current = true;
        onSwitchVideoRef.current(next);
      }
      return undefined;
    }
    const t = setTimeout(
      () => setCountdown((c) => (c == null ? null : c - 1)),
      1000,
    );
    return () => clearTimeout(t);
  }, [countdown]);

  // --- 1. INITIALIZATION EFFECT ---
  // Round-3 #4: deps are limited to the *identity* of the loaded video
  // (id + url + youtube branch). `savedTime` is read from a ref captured
  // when the modal opens or the video switches, NOT from live
  // watchHistory -- otherwise every onProgress tick would tear down and
  // rebuild the player, looping playback and leaking memory.
  useEffect(() => {
    let interval;
    // Cancellation for the deferred YouTube init below: without it, a
    // video switch / unmount during the "waiting for window.YT" retry loop
    // leaves an orphaned chain that later builds a zombie player into the
    // (by then reused or detached) container.
    let ytCancelled = false;
    let ytRetryTimer;
    const startAt = initialSavedTimeRef.current;
    pendingPlayRef.current = false;
    // Carried-over intent from an auto-advance / "Play now": the next video
    // should start on its own. Mobile browsers block a gesture-less
    // programmatic play once the tap's transient activation has expired (after
    // the 5s countdown), so instead we let the player AUTOPLAY MUTED at load --
    // the one form of autoplay mobile allows -- and unmute once it actually
    // starts. Consumed here; a video switch is an update so this runs once.
    const wantsAutoplay = autoplayNextRef.current;
    autoplayNextRef.current = false;
    setIsPlayerReady(false);
    setIsPlaying(false);
    setCurrentTime(startAt);
    lastTimeRef.current = startAt;

    const cleanupPlayers = () => {
      if (interval) clearInterval(interval);
      // On a YouTube->YouTube auto-advance we keep the player alive and swap
      // the video via loadVideoById, so its iframe stays user-activated --
      // the only way iOS/WebKit keeps autoplaying. Don't tear it down then.
      if (reuseRef.current) return;
      if (playerRef.current?.destroy) {
        try {
          playerRef.current.destroy();
        } catch (e) {}
        playerRef.current = null;
      }
      if (hlsRef.current) {
        hlsRef.current.destroy();
        hlsRef.current = null;
      }
      if (videoRef.current) {
        videoRef.current.pause();
        videoRef.current.removeAttribute("src");
        videoRef.current.load();
      }
    };

    // Effect cleanup: also stop the deferred-init retry chain. Kept separate
    // from cleanupPlayers, which loadYT reuses as an inner pre-create
    // teardown where cancelling would be wrong.
    const teardown = () => {
      ytCancelled = true;
      if (ytRetryTimer) clearTimeout(ytRetryTimer);
      cleanupPlayers();
    };

    if (isYouTube) {
      const videoId = video.url.includes("v=")
        ? video.url.split("v=")[1].split("&")[0]
        : video.url.split("/").pop().split("?")[0];

      // Per-second progress poll. Reads the *current* id via the ref so a
      // reused player (after loadVideoById) logs against the right video.
      const startProgressInterval = () =>
        setInterval(() => {
          const p = playerRef.current;
          if (
            p?.getCurrentTime &&
            p?.getPlayerState &&
            p.getPlayerState() === window.YT.PlayerState.PLAYING
          ) {
            const curr = p.getCurrentTime();
            setCurrentTime(curr);
            if (curr > maxReachedRef.current) maxReachedRef.current = curr;
            const dur = p.getDuration?.();
            if (dur) setDuration(dur);
            const delta = curr - lastTimeRef.current;
            const credit = creditedSeconds(delta, p.getPlaybackRate?.(), 2);
            if (credit > 0)
              onProgressRef.current?.(videoIdRef.current, curr, credit);
            lastTimeRef.current = curr;
          }
        }, 1000);

      // REUSE PATH: swap the video into the existing, already user-activated
      // player. The prior effect's cleanup left it alive (reuseRef). This is
      // what lets the next video keep autoplaying on iOS.
      const reuse = reuseRef.current && !!playerRef.current?.loadVideoById;
      reuseRef.current = false;
      if (reuse) {
        try {
          playerRef.current.loadVideoById({
            videoId,
            startSeconds: startAt > 0 ? startAt : 0,
          });
        } catch (e) {}
        setIsPlayerReady(true);
        interval = startProgressInterval();
        return teardown;
      }

      // The modal cannot assume the YouTube IFrame API is already on the
      // page. It used to only poll `window.YT` and wait, which meant that
      // on a fresh page load -- before any inline VideoYoutubePlayer had
      // mounted and injected the script -- `window.YT` stayed undefined
      // forever, `onReady` never fired, and the play tap (queued or not)
      // was silently dropped. This was the "refresh, expand a Not Started
      // video before playing -> never plays" repro. Inject the script here
      // too (idempotently) so the modal is self-sufficient.
      if (!window.YT || !window.YT.Player) {
        const existingScript = document.querySelector(
          'script[src="https://www.youtube.com/iframe_api"]',
        );
        if (!existingScript) {
          const tag = document.createElement("script");
          tag.src = "https://www.youtube.com/iframe_api";
          const firstScriptTag = document.getElementsByTagName("script")[0];
          if (firstScriptTag?.parentNode) {
            firstScriptTag.parentNode.insertBefore(tag, firstScriptTag);
          } else {
            document.head.appendChild(tag);
          }
        }
      }

      const loadYT = () => {
        if (ytCancelled) return;
        if (!window.YT || !window.YT.Player) {
          ytRetryTimer = setTimeout(loadYT, 100);
          return;
        }

        cleanupPlayers();

        playerRef.current = new window.YT.Player(ytContainerRef.current, {
          height: "100%",
          width: "100%",
          videoId: videoId,
          playerVars: {
            // Autoplay (muted) the next video on auto-advance -- mobile blocks
            // unmuted gesture-less playback. playsinline keeps iOS from forcing
            // fullscreen on play (which needs a gesture and would block
            // autoplay). The generated iframe already carries allow="autoplay".
            autoplay: wantsAutoplay ? 1 : 0,
            mute: wantsAutoplay ? 1 : 0,
            playsinline: 1,
            rel: 0,
            modestbranding: 1,
            controls: 0,
            disablekb: 1,
            enablejsapi: 1,
            origin: window.location.origin,
          },
          events: {
            onReady: (e) => {
              const ytDuration = e.target.getDuration();
              setDuration(ytDuration);
              if (startAt > 0) e.target.seekTo(startAt, true);
              if (wantsAutoplay) {
                // YouTube autoplays via the muted player vars above. Flag the
                // handshake so sound is restored on the first PLAYING (where
                // the browser permits it); set before isPlayerReady so the
                // volume-sync effect doesn't fight it.
                if (!isMutedRef.current) deferredMutedRef.current = true;
                onPlayRef.current?.(video.id);
              }
              setIsPlayerReady(true);
              // Captions already on (carried from the previous video or
              // chosen before ready): bring the module up on this player.
              // The selection itself lands via onApiChange / the probes.
              if (captionsOnRef.current) {
                pendingCaptionRef.current = true;
                try {
                  e.target.loadModule("captions");
                } catch (err) {}
              }
              if (pendingPlayRef.current) {
                pendingPlayRef.current = false;
                onPlayRef.current?.(video.id);
                // Deferred (gesture-less) play: start muted so the autoplay
                // policy allows it, then unmute once PLAYING fires (unless
                // the user explicitly muted).
                if (!isMutedRef.current) {
                  deferredMutedRef.current = true;
                  e.target.mute();
                }
                e.target.playVideo();
              }
            },
            onStateChange: (event) => {
              // This handler is created once and survives a loadVideoById
              // reuse, so it must read the CURRENT id via the ref.
              if (event.data === window.YT.PlayerState.PLAYING) {
                setIsPlaying(true);
                hasPlayedRef.current = true;
                setHasPlayedOnce(true); // re-arms the caption probe
                onPlayRef.current?.(videoIdRef.current);
                // The muted-autoplay handshake succeeded; restore sound now
                // that playback has actually begun.
                if (deferredMutedRef.current) {
                  deferredMutedRef.current = false;
                  if (!isMutedRef.current) event.target.unMute();
                }
              } else if (event.data === window.YT.PlayerState.PAUSED) {
                setIsPlaying(false);
              } else if (event.data === window.YT.PlayerState.ENDED) {
                setIsPlaying(false);
                onProgressRef.current?.(
                  videoIdRef.current,
                  event.target.getDuration(),
                  0,
                );
                beginCountdownRef.current?.();
              }
            },
            onApiChange: (event) => {
              // The caption module just came up -- this is the one reliable
              // moment its tracklist is actually populated.
              const list = readCaptionTracksRef.current?.(event.target) || [];
              if (pendingCaptionRef.current) {
                applyCaptionSelectionRef.current?.();
              }
              // Fast no-captions verdict: the module is up, the video has
              // played, and there are no tracks. One short re-check guards
              // against a list that populates a beat after the event.
              if (!list.length && hasPlayedRef.current) {
                setTimeout(() => {
                  const again =
                    readCaptionTracksRef.current?.(playerRef.current) || [];
                  if (!again.length) setCaptionStatus("none");
                }, 400);
              }
            },
          },
        });

        // Start the poll only AFTER the player exists. The old code started
        // it right after calling loadYT(); when player creation was deferred
        // (fresh page load, window.YT not yet available), the retry's
        // cleanupPlayers() above then cleared that already-running interval
        // and nothing restarted it -- video played, but the clock stayed at
        // 0:00 and onProgress (watch-time credit) never fired.
        interval = startProgressInterval();
      };

      loadYT();
    } else {
      const videoElement = videoRef.current;
      if (!videoElement) return;

      cleanupPlayers();

      // Deferred (gesture-less) HTML5 play: start muted so the autoplay
      // policy allows it, then unmute once playback begins (unless the
      // user explicitly muted). Mirrors the YouTube handshake above.
      const playDeferred = (el) => {
        if (!isMutedRef.current) {
          deferredMutedRef.current = true;
          el.muted = true;
        }
        el.play()
          .then(() => {
            if (deferredMutedRef.current) {
              deferredMutedRef.current = false;
              if (!isMutedRef.current) el.muted = false;
            }
          })
          .catch(() => {
            deferredMutedRef.current = false;
          });
      };

      if (
        video.url.includes(".m3u8") &&
        window.Hls &&
        window.Hls.isSupported()
      ) {
        const hls = new window.Hls();
        hls.loadSource(video.url);
        hls.attachMedia(videoElement);
        hlsRef.current = hls;

        hls.on(window.Hls.Events.MANIFEST_PARSED, () => {
          if (startAt > 0) videoElement.currentTime = startAt;
          setIsPlayerReady(true);
          if (pendingPlayRef.current || wantsAutoplay) {
            pendingPlayRef.current = false;
            onPlayRef.current?.(video.id);
            playDeferred(videoElement);
          }
        });
      } else {
        videoElement.src = video.url;
        if (startAt > 0) videoElement.currentTime = startAt;
        setIsPlayerReady(true);
        if (pendingPlayRef.current || wantsAutoplay) {
          pendingPlayRef.current = false;
          onPlayRef.current?.(video.id);
          playDeferred(videoElement);
        }
      }
    }

    return teardown;
    // `onProgress` and `onPlay` are read via refs; `initialSavedTimeRef`
    // is reset via the dedicated [video.id] effect above. Excluding them
    // here is intentional -- see Round-3 #4 comment. Captions never rebuild
    // the player; they're applied to the live player in section 3c.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [video.id, video.url, isYouTube]);

  // --- 2. PAUSE WHEN ANOTHER VIDEO STARTS PLAYING ---
  useEffect(() => {
    if (!isPlayerReady) return;
    if (!playingId || playingId === video.id) return;

    if (isYouTube && playerRef.current?.pauseVideo) {
      playerRef.current.pauseVideo();
    } else if (videoRef.current && !videoRef.current.paused) {
      videoRef.current.pause();
    }

    setIsPlaying(false);
  }, [playingId, video.id, isYouTube, isPlayerReady]);

  // --- 3. VOLUME SYNC ---
  useEffect(() => {
    isMutedRef.current = isMuted;
    if (!isPlayerReady) return;
    // Don't fight the in-flight muted-autoplay handshake; it will restore
    // sound itself once playback begins.
    if (deferredMutedRef.current) return;

    if (isYouTube && playerRef.current?.mute) {
      isMuted ? playerRef.current.mute() : playerRef.current.unMute();
    } else if (videoRef.current) {
      videoRef.current.muted = isMuted;
    }
  }, [isMuted, isPlayerReady, isYouTube]);

  // --- 3b. PLAYBACK-SPEED SYNC ---
  // Runs on rate change AND whenever the player becomes ready. A video switch
  // flips isPlayerReady false->true, so the chosen rate is re-applied to the
  // new (or reused) player without the viewer having to reselect it.
  useEffect(() => {
    playbackRateRef.current = playbackRate;
    if (!isPlayerReady) return;
    if (isYouTube && playerRef.current?.setPlaybackRate) {
      try {
        playerRef.current.setPlaybackRate(playbackRate);
      } catch (e) {}
    } else if (videoRef.current) {
      videoRef.current.playbackRate = playbackRate;
    }
  }, [playbackRate, isPlayerReady, isYouTube]);

  // --- 3c. CAPTIONS ---
  // YouTube captions are controlled at runtime through the player's caption
  // module (loadModule / setOption) on the LIVE player -- never by rebuilding
  // the iframe. The previous cc_load_policy / cc_lang_pref rebuild approach
  // was flaky: those vars are only *preferences* (Spanish silently fell back
  // to English) and the rebuild raced the module load (captions often never
  // appeared at all).
  //
  // The tracklist is only populated once the module is up, which the player
  // signals via onApiChange; selections made before that are parked in
  // pendingCaptionRef and applied there (probe timers below are the fallback
  // for players that never fire onApiChange). A picked language uses the
  // video's own track when one exists and YouTube's auto-translation of the
  // first track otherwise. Videos with no tracks at all surface as
  // "No captions available" once they have actually played.

  const baseLangOf = (code) => (code || "").toLowerCase().split(/[-_]/)[0];

  // Read the live player's tracklist into state; returns the raw list.
  // State writes are skipped while the list is still empty -- the
  // availability poll below calls this every 250ms, and a fresh [] on every
  // miss would re-render the modal for nothing (state starts as [] and is
  // reset on video change).
  const readCaptionTracks = (p) => {
    if (!p?.getOption) return [];
    let list = [];
    try {
      list = p.getOption("captions", "tracklist") || [];
    } catch (e) {
      return [];
    }
    const tracks = list
      .filter((t) => t && t.languageCode)
      .map((t) => ({
        code: t.languageCode,
        label: t.displayName || t.languageName || t.languageCode,
      }));
    if (tracks.length) {
      setCaptionTracks(tracks);
      setCaptionStatus("ready");
    }
    return list;
  };
  readCaptionTracksRef.current = readCaptionTracks;

  // Push the current on/off + language choice into the player. Safe to call
  // any time; parks itself in pendingCaptionRef while the module is still
  // coming up. Reads everything through refs so stale closures are harmless.
  //
  // Reliability: the player sometimes ACCEPTS a setOption yet keeps showing
  // the previous/default track (the "picked Spanish, still English" bug),
  // especially right after the module loads. So every selection is written
  // with the combined form (track + translationLanguage in ONE call, which
  // sticks far better than two sequential calls) and then VERIFIED shortly
  // after by reading the applied track back -- re-applying if it didn't take.
  const applyCaptionSelection = () => {
    const p = playerRef.current;
    if (!p?.getOption) return;
    captionVerifyTimersRef.current.forEach(clearTimeout);
    captionVerifyTimersRef.current = [];
    const on = captionsOnRef.current;
    const lang = captionLangRef.current;
    try {
      if (!on) {
        pendingCaptionRef.current = false;
        try {
          p.setOption("captions", "track", {});
        } catch (e) {}
        try {
          p.unloadModule("captions");
        } catch (e) {}
        return;
      }
      p.loadModule("captions");
      const list = p.getOption("captions", "tracklist") || [];
      if (!list.length) {
        pendingCaptionRef.current = true; // applied on onApiChange / probes
        return;
      }
      pendingCaptionRef.current = false;
      const want = lang?.code ? baseLangOf(lang.code) : null;
      // Videos can carry several tracks with the SAME language code (e.g. a
      // manual ".en" plus an auto-generated ASR "a.en"). A bare
      // {languageCode} setOption is then ambiguous -- the player may resolve
      // it to the ASR track and silently drop a requested translation (the
      // "IT Support Specialist stays English" bug). So selections always
      // pass the FULL track object from the tracklist, preferring the
      // manual track over ASR.
      const preferManual = (a, b) => (a.kind === "asr") - (b.kind === "asr");
      const matches = want
        ? list
            .filter((t) => baseLangOf(t.languageCode) === want)
            .sort(preferManual)
        : [];
      const exact = matches[0] || null;
      const target = exact || list.slice().sort(preferManual)[0];
      const translate = !!want && !exact;

      const write = () => {
        try {
          if (translate) {
            // No native track in that language: auto-translate `target`.
            p.setOption("captions", "track", {
              ...target,
              translationLanguage: {languageCode: lang.code},
            });
            // Older player builds honor only the standalone call; harmless
            // duplicate on current ones.
            p.setOption("captions", "translationLanguage", {
              languageCode: lang.code,
            });
          } else {
            // Native track (or the video's default). Clear any leftover
            // auto-translation from a previous choice.
            try {
              p.setOption("captions", "translationLanguage", null);
            } catch (e) {}
            p.setOption("captions", "track", {...target});
          }
        } catch (e) {}
      };
      write();

      const verify = () => {
        try {
          const cur = p.getOption("captions", "track") || {};
          const curBase = baseLangOf(cur.languageCode);
          const curTrans = baseLangOf(cur.translationLanguage?.languageCode);
          const ok = translate
            ? curTrans === want
            : curBase === baseLangOf(target.languageCode) &&
              !cur.translationLanguage;
          if (!ok) write();
        } catch (e) {}
      };
      [400, 1400].forEach((ms) => {
        captionVerifyTimersRef.current.push(setTimeout(verify, ms));
      });
    } catch (e) {}
  };
  applyCaptionSelectionRef.current = applyCaptionSelection;

  // Re-apply whenever the choice changes or a (new) player becomes ready.
  useEffect(() => {
    captionsOnRef.current = captionsOn;
    captionLangRef.current = captionLang;

    if (!isYouTube) {
      // HTML5 <video>: toggle native <track> text tracks in place, and let
      // the menu know what exists (none at all => "No captions available").
      const el = videoRef.current;
      if (!el) return;
      const tracks = el.textTracks || [];
      if (captionsOn) setCaptionStatus(tracks.length ? "ready" : "none");
      const want = captionLang?.code ? baseLangOf(captionLang.code) : null;
      let shown = false;
      for (let i = 0; i < tracks.length; i++) {
        const show =
          captionsOn &&
          !shown &&
          (!want || baseLangOf(tracks[i].language) === want);
        if (show) shown = true;
        tracks[i].mode = show ? "showing" : "disabled";
      }
      return;
    }
    if (!isPlayerReady || !playerRef.current) return;
    applyCaptionSelectionRef.current();
    // applyCaptionSelection reads everything through refs.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [captionsOn, captionLang, isYouTube, isPlayerReady]);

  // Availability probe. Runs as soon as the player is ready and re-arms when
  // playback first starts (YouTube only populates the tracklist after play),
  // NOT just when captions are turned on -- so by the time the viewer opens
  // the CC menu, a caption-less video already reads "No captions available"
  // instead of finding out only after a failed language pick. When captions
  // are off, the module is brought up silently (empty track) just to read
  // the list, then put away again.
  useEffect(() => {
    if (!isYouTube || !isPlayerReady) return undefined;
    if (captionStatus !== "unknown" && !pendingCaptionRef.current)
      return undefined;
    const p = playerRef.current;
    if (p?.getOption && !captionsOnRef.current) {
      try {
        p.loadModule("captions");
        p.setOption("captions", "track", {}); // module up, nothing displayed
      } catch (e) {}
    }
    const probe = (last) => {
      const list = readCaptionTracksRef.current?.(playerRef.current) || [];
      if (list.length) {
        if (pendingCaptionRef.current) {
          applyCaptionSelectionRef.current();
        } else if (!captionsOnRef.current) {
          // Probe-only visit -- put the module away again.
          try {
            playerRef.current?.unloadModule("captions");
          } catch (e) {}
        }
        return true;
      }
      if (last && hasPlayedRef.current) setCaptionStatus("none");
      return false;
    };
    // Poll fast, verdict slow: getOption is a cheap sync read, so a 250ms
    // cadence latches a pending pick almost as soon as the tracklist exists
    // (the old 700/1600/3200/5200 ladder could sit up to ~2s past it). The
    // 5.2s cutoff for the "none" verdict stays put -- slow connections
    // legitimately take seconds to surface the tracklist, and a false
    // "No captions available" is worse than a late one.
    const STEP_MS = 250;
    const DEADLINE_MS = 5200;
    let elapsed = 0;
    const interval = setInterval(() => {
      elapsed += STEP_MS;
      const last = elapsed >= DEADLINE_MS;
      if (probe(last) || last) clearInterval(interval);
    }, STEP_MS);
    return () => clearInterval(interval);
    // Probes read live state through refs; captionStatus is guarded above.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isYouTube,
    isPlayerReady,
    hasPlayedOnce,
    captionsOn,
    captionLang,
    video.id,
    captionStatus,
  ]);

  // Surface "no captions" the moment it's determined if the viewer had
  // already asked for them -- never make them reopen the menu to find out.
  useEffect(() => {
    if (captionStatus === "none" && captionsOnRef.current) {
      setCaptionNotice("No captions are available for this video.");
      const t = setTimeout(() => setCaptionNotice(null), 4000);
      return () => clearTimeout(t);
    }
    return undefined;
  }, [captionStatus]);

  // --- 4. FULLSCREEN ---
  // The CSS overlay (fixed inset-0) is the baseline and works everywhere,
  // including iOS Safari where the native Fullscreen API isn't available on
  // arbitrary elements / iframes. Where the native API IS available (Android
  // Chrome, desktop) we also request it, which additionally hides the browser
  // chrome. On iOS with a YouTube embed the OS URL bar can't be hidden by web
  // code -- that's a platform limitation we accept rather than work around.
  // Native exit (ESC / swipe) is synced back via the fullscreenchange listener.
  // lock=false is for the auto-rotate path: locking there would pin Android
  // to landscape and the rotate-back-to-portrait exit could never fire.
  const enterFullscreen = useCallback(({lock = true} = {}) => {
    setIsFullscreen(true);
    const el = playerWrapRef.current;
    const req = el?.requestFullscreen || el?.webkitRequestFullscreen;
    if (req) {
      try {
        const p = req.call(el);
        if (p?.catch) p.catch(() => {});
      } catch (e) {}
    }
    // Best-effort landscape lock; unsupported / rejected silently.
    if (lock && window.screen?.orientation?.lock) {
      window.screen.orientation.lock("landscape").catch(() => {});
    }
  }, []);

  const exitFullscreen = useCallback(() => {
    setIsFullscreen(false);
    const fsEl = document.fullscreenElement || document.webkitFullscreenElement;
    if (fsEl) {
      const exit = document.exitFullscreen || document.webkitExitFullscreen;
      if (exit) {
        try {
          const p = exit.call(document);
          if (p?.catch) p.catch(() => {});
        } catch (e) {}
      }
    }
    if (window.screen?.orientation?.unlock) {
      try {
        window.screen.orientation.unlock();
      } catch (e) {}
    }
  }, []);

  const toggleFullscreen = (e) => {
    e?.stopPropagation();
    if (isFullscreen) exitFullscreen();
    else enterFullscreen();
  };

  // Sync our state when the user exits native fullscreen via ESC, the system
  // back gesture, or the browser's own control.
  useEffect(() => {
    const onChange = () => {
      const fsEl =
        document.fullscreenElement || document.webkitFullscreenElement;
      if (!fsEl) setIsFullscreen(false);
    };
    document.addEventListener("fullscreenchange", onChange);
    document.addEventListener("webkitfullscreenchange", onChange);
    return () => {
      document.removeEventListener("fullscreenchange", onChange);
      document.removeEventListener("webkitfullscreenchange", onChange);
    };
  }, []);

  // Make sure we leave native fullscreen / release the orientation lock if the
  // modal closes or switches videos while fullscreen is active.
  useEffect(
    () => () => {
      const fsEl =
        document.fullscreenElement || document.webkitFullscreenElement;
      if (fsEl) {
        const exit = document.exitFullscreen || document.webkitExitFullscreen;
        if (exit) {
          try {
            const p = exit.call(document);
            if (p?.catch) p.catch(() => {});
          } catch (e) {}
        }
      }
      if (window.screen?.orientation?.unlock) {
        try {
          window.screen.orientation.unlock();
        } catch (e) {}
      }
    },
    [],
  );

  // Auto-fullscreen on rotation: a sideways phone is a clear "make it big"
  // signal, and the portrait sheet is unusable in landscape anyway (the video
  // fills the viewport and the scrubber/CC/speed controls end up below the
  // fold with no way to scroll). Rotating back to portrait exits. Touch
  // devices only, so resizing a desktop window doesn't hijack the layout.
  // Also runs once on open, for videos opened with the phone already
  // sideways. requestFullscreen may be rejected here (a rotation isn't a
  // user gesture) -- fine, the CSS overlay is the actual mechanism.
  useEffect(() => {
    // Also absent in jsdom, so tests exercise the modal without this effect.
    if (typeof window.matchMedia !== "function") return undefined;
    if (!window.matchMedia("(pointer: coarse)").matches) return undefined;
    // max-height keeps this to phones: an iPad held landscape shouldn't
    // force every video fullscreen.
    const mq = window.matchMedia(
      "(orientation: landscape) and (max-height: 500px)",
    );
    const onChange = (e) => {
      if (e.matches) enterFullscreen({lock: false});
      else exitFullscreen();
    };
    if (mq.matches) enterFullscreen({lock: false});
    // Older iOS Safari only has the addListener form.
    if (mq.addEventListener) mq.addEventListener("change", onChange);
    else mq.addListener(onChange);
    return () => {
      if (mq.removeEventListener) mq.removeEventListener("change", onChange);
      else mq.removeListener(onChange);
    };
  }, [enterFullscreen, exitFullscreen]);

  // Auto-hide the controls 3s after the last tap while playing. Any tap
  // sets controlsVisible back to true (see handleTogglePlay/handleSeek),
  // which restarts this timer. While paused, controls always stay visible.
  useEffect(() => {
    // Keep the controls up while the caption menu is open, otherwise it would
    // vanish mid-selection.
    if (isPlaying && controlsVisible && !showCaptionMenu) {
      const t = setTimeout(() => setControlsVisible(false), 3000);
      return () => clearTimeout(t);
    }
    if (!isPlaying) setControlsVisible(true);
  }, [isPlaying, controlsVisible, showCaptionMenu]);

  const handleTogglePlay = (e) => {
    e?.stopPropagation();
    setControlsVisible(true);
    if (!isPlayerReady) {
      // Player not initialized yet -- queue the intent so it fires as soon
      // as the player is ready instead of swallowing the tap.
      pendingPlayRef.current = true;
      return;
    }
    pendingPlayRef.current = false;

    if (isYouTube && playerRef.current) {
      if (isPlaying) {
        playerRef.current.pauseVideo();
      } else {
        onPlay(video.id);
        playerRef.current.playVideo();
      }
    } else if (videoRef.current) {
      if (isPlaying) {
        videoRef.current.pause();
      } else {
        onPlay(video.id);
        videoRef.current.play().catch(() => {});
      }
    }
  };

  const handleSeek = (e) => {
    setControlsVisible(true);
    let seekTo = parseFloat(e.target.value);

    // Orientation/intro videos: rewinding is fine, but you can't drag past the
    // furthest point already watched. Snap any forward jump back to that mark.
    if (isOrientation && seekTo > maxReachedRef.current) {
      seekTo = maxReachedRef.current;
    }

    if (isYouTube && playerRef.current) {
      playerRef.current.seekTo(seekTo, true);
    } else if (videoRef.current) {
      videoRef.current.currentTime = seekTo;
    }

    setCurrentTime(seekTo);
    lastTimeRef.current = seekTo;
  };

  // Cycle 1x -> 1.5x -> 2x -> 1x. The speed-sync effect pushes the new rate to
  // the live player.
  const cycleSpeed = (e) => {
    e?.stopPropagation();
    setControlsVisible(true);
    const idx = PLAYBACK_RATES.indexOf(playbackRate);
    setPlaybackRate(PLAYBACK_RATES[(idx + 1) % PLAYBACK_RATES.length]);
  };

  // One CC button opens the caption menu; the menu itself turns captions
  // on (per language) or off. No separate on/off + language buttons.
  const toggleCaptionMenu = (e) => {
    e?.stopPropagation();
    setControlsVisible(true);
    setShowCaptionMenu((s) => !s);
  };

  const chooseCaptionLang = (lang) => {
    setCaptionLang(lang);
    setCaptionsOn(true);
    setShowCaptionMenu(false);
  };

  const captionsOff = () => {
    setCaptionsOn(false);
    setShowCaptionMenu(false);
  };

  const getYouTubeThumbnail = (url) => {
    if (!url) return "/images/default_thumbnail.png";
    const regExp =
      /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
    const match = url.match(regExp);
    const videoId = match && match[2].length === 11 ? match[2] : null;

    return videoId
      ? `https://img.youtube.com/vi/${videoId}/hqdefault.jpg`
      : "/images/default_thumbnail.png";
  };

  // Tapping the video surface reveals the controls (and hides them again
  // while playing).
  //
  // The player surface is full-bleed at the top of the sheet (YouTube-style).
  // In fullscreen it escapes the sheet entirely and fills the viewport via a
  // fixed overlay, so a sideways phone shows edge-to-edge video.
  const playerWrapClass = isFullscreen
    ? "fixed inset-0 z-[10000] bg-black overflow-hidden select-none"
    : "relative w-full bg-black aspect-video overflow-hidden md:rounded-t-[2.5rem] select-none shrink-0";

  const modalContent = (
    // On mobile the overlay starts below the h-20 app header so the top bar
    // (menu / title / search / profile) stays visible; the sheet fills the rest
    // of the viewport and scrolls internally (player, title/status/category,
    // then the "Up next" list) with the dashboard never shown behind it.
    // Desktop (md:) keeps the centered dialog.
    <div className="fixed inset-x-0 bottom-0 top-20 md:inset-0 z-[9999] flex md:items-center md:justify-center md:p-4 bg-black md:bg-black/70">
      <div className="bg-white w-full h-full md:h-auto md:max-w-2xl md:max-h-[95vh] md:rounded-[2.5rem] shadow-2xl relative overflow-hidden flex flex-col">
        {/* --- VIDEO (full-bleed, first) --- */}
        <div ref={playerWrapRef} className={playerWrapClass}>
          {isYouTube ? (
            <div ref={ytContainerRef} className="absolute inset-0" />
          ) : (
            <video
              ref={videoRef}
              className="absolute inset-0 w-full h-full object-contain"
              playsInline
              onPlay={() => {
                setIsPlaying(true);
                onPlay(video.id);
              }}
              onPause={() => setIsPlaying(false)}
              onEnded={(e) => {
                setIsPlaying(false);
                onProgress(video.id, e.target.duration, 0);
                beginCountdown();
              }}
              onTimeUpdate={(e) => {
                const curr = e.target.currentTime;
                setCurrentTime(curr);
                if (curr > maxReachedRef.current) maxReachedRef.current = curr;
                const delta = curr - lastTimeRef.current;
                const credit = creditedSeconds(delta, e.target.playbackRate, 1);

                if (credit > 0) {
                  onProgress(video.id, curr, credit);
                }

                lastTimeRef.current = curr;
              }}
              onLoadedMetadata={(e) => {
                setDuration(e.target.duration);
                const startAt = initialSavedTimeRef.current;
                if (startAt > 0) {
                  e.target.currentTime = startAt;
                }
              }}
            />
          )}

          {/* Tap surface: tapping the video toggles play/pause. If the caption
              menu is open, the first tap just dismisses it. */}
          <div
            className="absolute inset-0 z-10 cursor-pointer"
            onClick={(e) => {
              if (showCaptionMenu) {
                setShowCaptionMenu(false);
                return;
              }
              handleTogglePlay(e);
            }}
          />

          {/* Close button (top-right) */}
          <div
            className={`absolute top-0 left-0 right-0 p-3 md:p-4 flex justify-end bg-gradient-to-b from-black/60 to-transparent z-20 transition-opacity duration-300 ${
              controlsVisible ? "opacity-100" : "opacity-0 pointer-events-none"
            }`}
          >
            <button
              onClick={(e) => {
                e.stopPropagation();
                if (isFullscreen) exitFullscreen();
                onClose();
              }}
              aria-label="Close video"
              className="p-2 rounded-full bg-black/40 hover:bg-black/60 text-white transition-colors"
            >
              <X size={22} />
            </button>
          </div>

          {/* Center play button (only while paused) */}
          {!isPlaying && (
            <div className="absolute inset-0 z-20 flex items-center justify-center pointer-events-none">
              <button
                onClick={handleTogglePlay}
                aria-label="Play"
                className="pointer-events-auto w-16 h-16 rounded-full bg-black/50 hover:bg-black/70 text-white flex items-center justify-center transition-colors"
              >
                <Play size={30} fill="currentColor" className="ml-1" />
              </button>
            </div>
          )}

          {/* Bottom control bar */}
          <div
            className={`absolute bottom-0 left-0 right-0 px-4 pb-4 pt-8 md:px-6 bg-gradient-to-t from-black/90 to-transparent z-20 transition-opacity duration-300 ${
              controlsVisible ? "opacity-100" : "opacity-0 pointer-events-none"
            }`}
          >
            <input
              type="range"
              min="0"
              max={duration || 0}
              value={currentTime || 0}
              onChange={handleSeek}
              onClick={(e) => e.stopPropagation()}
              aria-label={isOrientation ? "Seek (rewind only)" : "Seek"}
              className="w-full h-1 accent-orange-500 mb-1"
            />

            {isOrientation && (
              <p className="flex items-center gap-1 text-[11px] text-white/70 mb-2">
                <Lock size={11} />
                You can rewind, but this orientation video can&rsquo;t be
                skipped ahead.
              </p>
            )}

            <div className="flex items-center justify-between text-white text-sm mt-2">
              <div className="flex items-center gap-4 sm:gap-5">
                <button
                  onClick={handleTogglePlay}
                  aria-label={isPlaying ? "Pause" : "Play"}
                >
                  {isPlaying ? (
                    <Pause size={20} fill="currentColor" />
                  ) : (
                    <Play size={20} fill="currentColor" />
                  )}
                </button>

                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setIsMuted(!isMuted);
                  }}
                  aria-label={isMuted ? "Unmute" : "Mute"}
                >
                  {isMuted ? <VolumeX size={20} /> : <Volume2 size={20} />}
                </button>

                <button
                  onClick={cycleSpeed}
                  aria-label={`Playback speed ${playbackRate}x, tap to change`}
                  className="min-w-[2.5rem] px-1.5 py-0.5 rounded-md text-xs font-bold tabular-nums bg-white/15 hover:bg-white/25 transition-colors"
                >
                  {playbackRate}x
                </button>

                <span className="font-medium tabular-nums">
                  {formatPlaybackTime(currentTime)} /{" "}
                  {formatPlaybackTime(duration)}
                </span>
              </div>

              <div className="flex items-center gap-4 sm:gap-5">
                {/* Captions: one CC button opening the language menu.
                    "No captions available" is shown when the video provably
                    has no tracks (captionStatus === 'none'). */}
                <div className="relative">
                  <button
                    onClick={toggleCaptionMenu}
                    aria-label="Captions"
                    aria-haspopup="menu"
                    aria-expanded={showCaptionMenu}
                    aria-pressed={captionsOn}
                    className={`min-w-[2.5rem] px-1.5 py-0.5 rounded-md text-xs font-bold transition-colors ${
                      captionsOn && captionStatus !== "none"
                        ? "bg-white/15 text-orange-400 hover:bg-white/25"
                        : "bg-white/15 text-white hover:bg-white/25"
                    }`}
                  >
                    CC
                  </button>

                  {showCaptionMenu && (
                    <div
                      onClick={(e) => e.stopPropagation()}
                      role="menu"
                      className="absolute bottom-full right-0 mb-2 w-56 max-h-64 overflow-y-auto rounded-xl bg-slate-900/95 backdrop-blur border border-white/10 shadow-2xl py-1 text-sm"
                    >
                      {captionStatus === "none" ? (
                        <p className="px-3 py-2 text-white/70">
                          No captions are available for this video.
                        </p>
                      ) : (
                        (() => {
                          // Off / English / Español only -- the program's two
                          // languages. Other tracks the video carries are
                          // deliberately not listed (UI clutter for learners);
                          // variants like es-419 still satisfy Español via
                          // base-language matching.
                          const items = [
                            {
                              key: "en",
                              label: "English",
                              lang: {code: "en", label: "English"},
                            },
                            {
                              key: "es",
                              label: "Español (Spanish)",
                              lang: {code: "es", label: "Español"},
                            },
                          ];
                          const activeBase = captionsOn
                            ? baseLangOf(captionLang?.code)
                            : null;
                          const rows = [
                            <button
                              key="off"
                              role="menuitemradio"
                              aria-checked={!captionsOn}
                              onClick={captionsOff}
                              className={`w-full text-left px-3 py-2 hover:bg-white/10 transition-colors ${
                                !captionsOn
                                  ? "text-orange-400 font-semibold"
                                  : "text-white"
                              }`}
                            >
                              Off
                            </button>,
                            ...items.map((it) => {
                              const active =
                                captionsOn &&
                                activeBase === baseLangOf(it.lang.code);
                              return (
                                <button
                                  key={it.key}
                                  role="menuitemradio"
                                  aria-checked={active}
                                  onClick={() => chooseCaptionLang(it.lang)}
                                  className={`w-full text-left px-3 py-2 hover:bg-white/10 transition-colors ${
                                    active
                                      ? "text-orange-400 font-semibold"
                                      : "text-white"
                                  }`}
                                >
                                  {it.label}
                                </button>
                              );
                            }),
                          ];
                          return rows;
                        })()
                      )}
                      {captionStatus === "ready" &&
                        captionsOn &&
                        captionLang?.code &&
                        !captionTracks.some(
                          (t) =>
                            baseLangOf(t.code) === baseLangOf(captionLang.code),
                        ) && (
                          <p className="px-3 py-1.5 text-[11px] text-white/50 border-t border-white/10">
                            Translated automatically by YouTube.
                          </p>
                        )}
                      {captionStatus === "unknown" && (
                        <p className="px-3 py-1.5 text-[11px] text-white/50 border-t border-white/10">
                          Checking this video&rsquo;s captions&hellip;
                        </p>
                      )}
                    </div>
                  )}
                </div>

                <button
                  onClick={toggleFullscreen}
                  aria-label={isFullscreen ? "Exit fullscreen" : "Fullscreen"}
                >
                  {isFullscreen ? (
                    <Minimize size={20} />
                  ) : (
                    <Maximize size={20} />
                  )}
                </button>
              </div>
            </div>
          </div>

          {/* Transient caption notice: appears immediately when the
              no-captions verdict lands after the viewer picked a language. */}
          {captionNotice && (
            <div
              role="status"
              className="absolute bottom-16 left-1/2 -translate-x-1/2 z-20 px-3 py-1.5 rounded-lg bg-black/80 text-white text-sm whitespace-nowrap pointer-events-none"
            >
              {captionNotice}
            </div>
          )}

          {/* Auto-advance countdown: shown over the player when a video ends
              and the playlist has a next video. */}
          {countdown != null && nextVideo && (
            <div className="absolute inset-0 z-30 bg-black/85 flex flex-col items-center justify-center text-center px-6 text-white">
              <p className="text-[11px] font-bold uppercase tracking-wide text-white/60 mb-2">
                Up next
              </p>
              <p className="text-base md:text-lg font-semibold mb-1 line-clamp-2 max-w-md">
                {nextVideo.title}
              </p>
              <p className="text-sm text-white/70 mb-5">
                Playing in {countdown}s
              </p>
              <div className="flex items-center gap-3">
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setCountdown(null);
                    if (onSwitchVideo) {
                      autoplayNextRef.current = true;
                      if (canReuse(nextVideo)) reuseRef.current = true;
                      onSwitchVideo(nextVideo);
                    }
                  }}
                  className="px-5 py-2 rounded-full bg-white text-slate-900 font-semibold text-sm hover:bg-white/90 transition-colors"
                >
                  Play now
                </button>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setCountdown(null);
                  }}
                  className="px-5 py-2 rounded-full bg-white/15 text-white font-semibold text-sm hover:bg-white/25 transition-colors"
                >
                  Cancel
                </button>
              </div>
            </div>
          )}
        </div>

        {/* --- INFO + SUGGESTIONS (scrollable) --- */}
        <div className="flex-1 overflow-y-auto min-h-0">
          <div className="px-5 md:px-8 pt-4 md:pt-6">
            <h2 className="text-base md:text-xl font-bold text-slate-900 mb-1">
              {video.title}
            </h2>
            {channel && (
              <p className="text-slate-700 text-sm font-semibold mb-1.5">
                {channel}
              </p>
            )}
            <span
              className={`inline-flex items-center gap-1.5 px-3 py-0.5 rounded-full text-[11px] font-semibold border whitespace-nowrap ${classes}`}
            >
              <span className={`w-1.5 h-1.5 rounded-full ${dot}`} />
              {label}
            </span>
            <p className="text-slate-400 text-xs md:text-sm mt-1.5">
              Category:{" "}
              <span className="text-slate-600 font-medium">
                {video.category}
              </span>
            </p>
          </div>

          <div className="px-5 md:px-8 pt-6 pb-8">
            <h4 className="text-lg font-bold text-slate-900 mb-4">
              {fromQueue ? "Up next" : "Suggestions"}
              {suggestions.length > 0 && (
                <span className="ml-2 text-sm font-medium text-slate-400">
                  {suggestions.length}
                </span>
              )}
            </h4>

            {suggestions.length === 0 ? (
              <p className="text-sm text-slate-400">
                You&rsquo;ve reached the end of this playlist.
              </p>
            ) : (
              // Full list; it lives in the panel's own scroll. Thumbnails are
              // lazy <img> so a long playlist doesn't fetch every image at once.
              <div className="space-y-3">
                {suggestions.map((v) => {
                  const vSavedTime = getFurthestPosition(watchHistory[v.id]);
                  const vProgress = getProgressData(vSavedTime, v);
                  const vDuration = Number(v?.videoLength) || 0;
                  const vPct =
                    vDuration > 0
                      ? Math.min(
                          100,
                          Math.max(0, (vSavedTime / vDuration) * 100),
                        )
                      : 0;

                  // Swap the open modal to this video (keeps the playlist
                  // queue via the parent's onSwitchVideo).
                  const handleSwitch = () => {
                    if (onSwitchVideo) onSwitchVideo(v);
                    else onClose?.();
                  };

                  return (
                    <button
                      type="button"
                      key={v.id}
                      onClick={handleSwitch}
                      className="w-full text-left flex items-start gap-3 group cursor-pointer focus:outline-none focus:ring-2 focus:ring-indigo-300 rounded-xl p-1 -m-1"
                    >
                      <div className="relative w-32 sm:w-36 aspect-video rounded-lg overflow-hidden bg-slate-200 shrink-0">
                        <img
                          src={getYouTubeThumbnail(v.url)}
                          loading="lazy"
                          decoding="async"
                          className="w-full h-full object-cover"
                          alt=""
                        />
                        {vPct > 0 && (
                          <span className="absolute bottom-0 left-0 right-0 h-1 bg-white/30">
                            <span
                              className="block h-full bg-orange-500"
                              style={{width: `${vPct}%`}}
                            />
                          </span>
                        )}
                      </div>

                      <div className="min-w-0 flex-1 pt-0.5">
                        <h5 className="font-semibold text-slate-800 text-sm leading-snug line-clamp-2 group-hover:text-blue-600 transition-colors">
                          {v.title}
                        </h5>
                        <p className="text-slate-400 text-[11px] truncate mt-0.5">
                          {v.category}
                        </p>
                        <span
                          className={`inline-flex items-center gap-1.5 px-2 py-0.5 mt-1 rounded-full text-[10px] font-bold border whitespace-nowrap ${vProgress.classes}`}
                        >
                          <span
                            className={`w-1.5 h-1.5 rounded-full ${vProgress.dot}`}
                          />
                          {vProgress.label}
                        </span>
                      </div>
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
};

export default VideoModal;
